import _ from 'lodash'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { useRequestHandler } from '@/hooks/useRequestHandler'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import {
  deleteAvailability,
  selectMeetingLinkDeleteRequestTracker,
  selectMeetingLinkUpdateRequestTracker,
  updateAvailability,
} from '@/redux/reducers/meeting-link.reducer'

import { getGeocode, getLatLng } from 'use-places-autocomplete'

import { AvailabilitiesTable, AvailabilityData } from '../components/AvailabilitiesTable'
import { useGetMeetingLinkQuery } from '@/core/services/meeting-link'
import { AvailabilityMeetingPlace } from '@/core/models/meeting-link.model'
import { StaffData } from '../components/StaffSelectionTable'

export default function AvailabilitiesTableContainer() {
  const { meetingLinkId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)

  const { data: meetingLink } = useGetMeetingLinkQuery(meetingLinkId ?? '', { skip: !meetingLinkId })

  const availabilitiesData = useMemo(
    () =>
      _.map(meetingLink?.availabilities, (availability) => ({
        id: availability._id,
        address: availability.address,
        additionalAddress: availability.additionalAddress,
        avgMeetingDuration: availability.avgMeetingDuration,
        isRecurrence: availability.isRecurrence,
        meetingPlace: availability.meetingPlace,
        notes: availability.notes,
        recurrenceDays: _.map(availability.recurrenceDays, (recurrenceDay) => ({
          day: recurrenceDay.day,
          periods: _.map(recurrenceDay.periods, (period) => ({
            open: dayjs.duration(period.open, 'minutes').format('HH:mm'),
            close: dayjs.duration(period.close, 'minutes').format('HH:mm'),
          })),
        })),
        scheduledDays: _.map(availability.scheduledDays, (scheduledDay) => ({
          day: dayjs(scheduledDay.day, 'DD/MM/YYYY').toDate(),
          periods: _.map(scheduledDay.periods, (period) => ({
            open: dayjs.duration(period.open, 'minutes').format('HH:mm'),
            close: dayjs.duration(period.close, 'minutes').format('HH:mm'),
          })),
        })),
        specialities: availability.specialities,
        paymentConfig: availability.paymentConfig ? {
          requiresPayment: availability.paymentConfig.requiresPayment ?? false,
          basePrice: availability.paymentConfig.basePrice,
          paymentReceiver: typeof availability.paymentConfig.paymentReceiver === 'object'
            ? ((availability.paymentConfig.paymentReceiver as any)?._id || '')
            : (availability.paymentConfig.paymentReceiver || ''),
          priceModifiers: availability.paymentConfig.priceModifiers?.map(modifier => ({
            meetingPlace: modifier.meetingPlace,
            modifier: modifier.modifier
          }))
        } : undefined,
        serviceArea: availability.serviceArea ? {
          ...availability.serviceArea,
          center: availability.serviceArea.center ? {
            ...availability.serviceArea.center,
            description: availability.serviceArea.center.description
              || (availability.serviceArea.center.lat != null && availability.serviceArea.center.lng != null
                ? `${availability.serviceArea.center.lat.toFixed(4)}, ${availability.serviceArea.center.lng.toFixed(4)}`
                : undefined),
          } : undefined,
        } : undefined,
        unavailabilities: availability.unavailabilities,
      })),
    [meetingLink]
  )

  const isMeetingLinkCreator = useMemo(() => authUser?._id === meetingLink?.createdBy, [authUser, meetingLink])

  const meetingLinkSpecialities = useMemo(() => _.map(meetingLink?.specialities, 'name'), [meetingLink])

  const staffs = useMemo(() => {
    if (!meetingLink?.staffs) return []
    return meetingLink.staffs.map((staff): StaffData => ({
      id: staff._id,
      cps: staff.cps || '',
      name: staff.userInfo.name,
      surname: staff.userInfo.surname,
      status: staff.status,
      isSameEtablissement: true, // Since they're part of the same meeting link, they're considered part of the same establishment
      stripe: staff.stripe,
      stripeActivated: staff.stripeActivated
    }))
  }, [meetingLink])

  const deleteAvailabilityRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.availability.delete.success'), { variant: 'success' })
    },
    onError: () => {
      enqueueSnackbar(t('notification.availability.delete.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectMeetingLinkDeleteRequestTracker,
  })

  const updateAvailabilityRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.availability.update.success'), { variant: 'success' })
    },
    onError: () => {
      enqueueSnackbar(t('notification.availability.update.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectMeetingLinkUpdateRequestTracker,
  })

  const handleDeleteAvailability = (availabilityId: string) => {
    if (!meetingLinkId) return

    deleteAvailabilityRequestHandler.dispatch(
      deleteAvailability({
        meetingId: meetingLinkId,
        availabilityId,
      })
    )
  }

  const handleUpdateAvailability = async (values: AvailabilityData) => {
    if (!meetingLinkId) return

    const { id: availabilityId, unavailabilities: _unavailabilities, ...updateAvailabilityDto } = values

    let serviceArea: { allowedPostalCodes?: string[]; center?: Record<string, unknown> | null; radiusKm?: number } | undefined = undefined
    if (updateAvailabilityDto.meetingPlace === AvailabilityMeetingPlace.AT_HOME && updateAvailabilityDto.serviceArea) {
      const allowedPostalCodes = typeof updateAvailabilityDto.serviceArea.allowedPostalCodes === 'string'
        ? (updateAvailabilityDto.serviceArea.allowedPostalCodes as string).split(',').map(s => s.trim()).filter(Boolean)
        : updateAvailabilityDto.serviceArea.allowedPostalCodes

      let center = updateAvailabilityDto.serviceArea.center
      if (center?.placeId && (center.lat == null || center.lng == null)) {
        try {
          const results = await getGeocode({ placeId: center.placeId })
          const { lat, lng } = getLatLng(results[0])
          center = { ...center, lat, lng }
        } catch {
          enqueueSnackbar(t('notification.availability.update.error'), { variant: 'error' })
          return
        }
      }

      serviceArea = { allowedPostalCodes, center, radiusKm: updateAvailabilityDto.serviceArea.radiusKm }
    }

    updateAvailabilityRequestHandler.dispatch(
      updateAvailability({
        meetingLinkId,
        availabilityId,
        updateAvailabilityDto: {
          ...updateAvailabilityDto,
          avgMeetingDuration: Number(updateAvailabilityDto.avgMeetingDuration),
          recurrenceDays: _.map(values.recurrenceDays, (recurrenceDay) => ({
            day: recurrenceDay.day,
            periods: _.map(recurrenceDay.periods, (period) => ({
              open: dayjs.duration({ hours: Number(period.open.split(':')[0]), minutes: Number(period.open.split(':')[1]) }).asMinutes(),
              close: dayjs.duration({ hours: Number(period.close.split(':')[0]), minutes: Number(period.close.split(':')[1]) }).asMinutes(),
            })),
            timeZone: dayjs.tz.guess(),
          })),
          scheduledDays: _.map(values.scheduledDays, (scheduledDay) => ({
            day: dayjs(scheduledDay.day).format('DD/MM/YYYY'),
            format: 'DD/MM/YYYY',
            periods: _.map(scheduledDay.periods, (period) => ({
              open: dayjs.duration({ hours: Number(period.open.split(':')[0]), minutes: Number(period.open.split(':')[1]) }).asMinutes(),
              close: dayjs.duration({ hours: Number(period.close.split(':')[0]), minutes: Number(period.close.split(':')[1]) }).asMinutes(),
            })),
            timeZone: dayjs.tz.guess(),
          })),
          serviceArea,
          paymentConfig: updateAvailabilityDto.paymentConfig ? {
            ...updateAvailabilityDto.paymentConfig,
            paymentReceiver: typeof updateAvailabilityDto.paymentConfig.paymentReceiver === 'object'
              ? ((updateAvailabilityDto.paymentConfig.paymentReceiver as any)?._id || (updateAvailabilityDto.paymentConfig.paymentReceiver as any)?.id || null)
              : (updateAvailabilityDto.paymentConfig.paymentReceiver || null),
            basePrice: Number(updateAvailabilityDto.paymentConfig.basePrice),
            priceModifiers: updateAvailabilityDto.paymentConfig.priceModifiers?.map(modifier => ({
              meetingPlace: modifier.meetingPlace,
              modifier: Number(modifier.modifier)
            }))
          } : undefined
        },
      })
    )
  }

  return (
    <AvailabilitiesTable
      data={availabilitiesData}
      disableActions={!isMeetingLinkCreator}
      isUpdating={updateAvailabilityRequestHandler.requestTracker.loading}
      meetingLinkId={meetingLinkId}
      meetingLinkSpecialities={meetingLinkSpecialities}
      onDeleteAvailability={handleDeleteAvailability}
      onUpdateAvailability={handleUpdateAvailability}
      totalCount={availabilitiesData.length}
      staffs={staffs}
    />
  )
}
